import { CircleCheck, CircleDashed, CircleX, TriangleAlert } from "lucide-mui";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import type { ReactNode } from "react";

/**
 * How a connection, credential or feed is doing. `ok` is the only kind that
 * needs nothing from anyone; `attention` means it is set up but needs a look;
 * `error` means it is failing; `off` means nothing is set up (or can't be).
 */
export type StatusKind = "ok" | "attention" | "error" | "off" | "loading";

export interface Status {
  kind: StatusKind;
  label?: string;
}

const SIZE = 20;

/** The one icon per status kind, wherever a connection's health is shown. */
export default function StatusIcon({ kind, label, size = SIZE }: Status & { size?: number }) {
  let icon: ReactNode;
  if (kind === "loading") {
    icon = <CircularProgress size={size - 4} sx={{ m: "2px" }} />;
  } else if (kind === "ok") {
    icon = <CircleCheck sx={{ fontSize: size, color: "success.main" }} />;
  } else if (kind === "attention") {
    icon = <TriangleAlert sx={{ fontSize: size, color: "warning.main" }} />;
  } else if (kind === "error") {
    icon = <CircleX sx={{ fontSize: size, color: "error.main" }} />;
  } else {
    icon = <CircleDashed sx={{ fontSize: size, color: "text.disabled" }} />;
  }
  const wrapped = (
    <Box
      component="span"
      role={label ? "img" : undefined}
      aria-label={label}
      sx={{ display: "inline-flex", flexShrink: 0 }}
    >
      {icon}
    </Box>
  );
  return label ? <Tooltip title={label}>{wrapped}</Tooltip> : wrapped;
}

/** A status icon followed by its label, for a row's status column. */
export function StatusLabel({ kind, label }: Status & { label: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <StatusIcon kind={kind} size={16} />
      <Box
        component="span"
        sx={{
          color: kind === "attention" || kind === "error" ? "text.primary" : "text.secondary",
          fontWeight: kind === "attention" || kind === "error" ? 600 : undefined,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}
